import { List, Divider, Box, Typography } from "@mui/material";
import React from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { StatusEnum } from "../../common/IExportNotificationMsg";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useDeleteNotificationMessage } from "../../queries/notification-messages/UseDeleteNotificationMessage";
import { useNotificationMessages } from "../../queries/notification-messages/UseNotificationMessagess";

import { useQueryNotifications } from "../../queries/UseQueryNotifications";

import { INotificationMessageModel, NotificationMessageTypeEnum } from "../../services/notification-message-service/Models";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import LoadingZone from "../loading-zone/LoadingZone";
import { UserMessageActionEnum, UserMessageListItem } from "./UserMessageListItem";
import { isMobile, isTablet } from 'react-device-detect';
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import LiveDataService from "../../services/live-data-service/LiveDataService";
import { CommandStatusEnum, LiveDataCommandEnum } from "../../services/live-data-service/Models";
import signalRHubConfiguration, { SignalRHubEnum } from "../../hooks/use-signalr/SignalRHubInfo";
import { useSignalR } from "../../hooks/use-signalr/UseSignalR";
import settings from "../../app-settings.json";
import { useUserContext } from "../../contexts/user-context/UserContext";
import GraphViewDialog, { bindGraphViewDialog, useGraphViewDialogState } from "../object-graph/graph-view-dialog/GraphViewDialog";
import { bindGraphReportViewDialog, GraphReportViewDialog, useGraphReportViewDialogState } from "../graph-report/graph-report-view-dialog/GraphReportViewDialog";
// import ReportViewDialog, { bindReportViewDialog, useReportViewDialogState } from "../../views/report-view/configuration-editor/ReportViewDialog";
import ReportViewDialog, { bindReportViewDialog, useReportViewDialogState } from "../reporting-view-content/configuration-editor/ReportViewDialog";
import { format } from "date-fns";
import { RestError } from "../../services/rest-service/RestService";
import storageService from "../../services/storage/storage-service/StorageService";
// import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import DataFileImportDataService from "../../services/data-file-import-data-service/DataFileImportDataService";
import DataFileImportListUtils from "../../views/data-file-import-view/data-file-import-list/DataFileImportListUtils";

export const MESSAGEIDKEY = 'usermessageid';

export type UserMessageListProps = {
    onClose: () => void,
};

export default function UserMessageList({ onClose }: UserMessageListProps) {

    const isPhone = isMobile && !isTablet;
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [progressState] = useProgressContext();
    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const [, setAppNotificationState] = useAppNotificationContext();
    // const navigateTo = useNavigateTo();

    const fileDownloaderRef = useRef<FileDownloaderType>(null);

    // #region Dialogs

    const graphViewDialog = useGraphViewDialogState();
    const graphReportViewDialog = useGraphReportViewDialogState();
    const reportViewDialog = useReportViewDialogState();

    // #endregion Dialogs


    const {
        data,
        status,
        refetch
    } = useQueryNotifications(useNotificationMessages)({
        groupID: routeContext.groupID as number
    });


    const { mutate: deleteMessage } = useQueryNotifications(useDeleteNotificationMessage)({
        groupID: routeContext.groupID as number,
        onSuccess: (data, item) => {
            acknowledge(t('deleteSuccessMessage', { itemName: item.description }))
        }
    });


    const hubConfiguration = signalRHubConfiguration.find(o => o.id === SignalRHubEnum.UserMessage);
    useSignalR({
        url: `${settings.apiBaseUrl}${hubConfiguration?.path}`,
        customerID: userState.customer?.id,
        methodName: hubConfiguration?.methodName as string,
        onCallback: () => { refetch() },
        offline: false
    });


    const listItems = data?.sort((a, b) => (b.messageDate?.valueOf() ?? 0) - (a.messageDate?.valueOf() ?? 0)).filter(o => o.command !== LiveDataCommandEnum.GetPollingStatistics);

    async function getDeleteConfirmation(item: INotificationMessageModel) {

        const title = item.messageType === NotificationMessageTypeEnum.ExportMessage ?
            t('notificationMessage.export') :
            item.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage ?
                t('notificationMessage.liveData') :
                item.messageType === NotificationMessageTypeEnum.ReportingJSON ?
                    t('notificationMessage.report') :
                    item.messageType === NotificationMessageTypeEnum.QuickTrendJSON ?
                        t('notificationMessage.graphReport') :
                        '';



        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: title }),
            //confirmationText: `${item.description}. ${t("deleteConfirmation.confirmationText")}`,
            confirmationText: item.description,
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    async function confirmAndDeleteFormInstance(item: INotificationMessageModel) {
        const canDelete = await getDeleteConfirmation(item);

        if (canDelete) {
            deleteMessage(item);
        }
    }

    const onDisplayGraphView = async (payload: INotificationMessageModel) => {

        await graphViewDialog.open(
            {
                fileStoreID: payload.id,
                messageDate: payload.messageDate
            });
    };

    const onDisplayGraphReportView = async (payload: INotificationMessageModel) => {

        await graphReportViewDialog.open(
            {
                fileStoreId: payload.id,
                messageDate: payload.messageDate
            });
    };

    const onDisplayReportView = async (payload: INotificationMessageModel) => {

        await reportViewDialog.open({
            fileStoreID: payload.id,
            title: `${payload.description} - ${t("reporting.created")} ${format(payload?.messageDate as Date, "P pp")}`
        });
    };


    const onDisplayDataFileImportLog = async (payload: INotificationMessageModel) => {

        try {

            const logEntries = await DataFileImportDataService.getProcessImportLog(routeContext.groupID as number, payload.id);

            if (logEntries) {

                const body = DataFileImportListUtils.shapeImportLogs(logEntries);

                await confirm({
                    dialogTitle: t("dataFileImport.processImportLog.title"),
                    yesButtonText: t("buttons.okay"),
                    confirmationBodyComponent: body,
                    showConfirmationText: false,
                    showYesButton: true,
                    showNoButton: false,
                    showCloseButton: false
                });
            }

        } catch (error) {

            const err =  error as RestError;
               
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: err.message,
                show: true,
                error: err
            });

        }
    };


    async function onDisplayLiveDataStatus(item: INotificationMessageModel) {

        try {

            const livedataStatus = await LiveDataService.getStatus(routeContext.groupID as number, item.requestID);

            if (livedataStatus) {
                const statusItems = livedataStatus.deviceStatusList.flatMap(o => o.statusItems);
                const successCount = statusItems.filter(o => o.status === CommandStatusEnum.Success).length;
                const failedCount = statusItems.filter(o => o.status === CommandStatusEnum.Failed).length;

                setAppNotificationState({
                    title: t(item.command === LiveDataCommandEnum.DemandScan ? 'livedata.demandscanFailed' : 'livedata.writeDownFailed').replace('{0}', item.description),
                    notificationType: AppNotificationTypeEnum.Error,
                    message:
                        <Box>
                            {livedataStatus.deviceStatusList[0].deviceStatusMessage}


                            <Typography
                                sx={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto', mt: 1 }}
                                component="span"
                                variant="body2"
                            >
                                {t('livedata.successText', {
                                    count: successCount,
                                    total: statusItems.length
                                })}
                            </Typography>

                            <Typography
                                sx={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}
                                component="span"
                                variant="body2"
                            >
                                {t('livedata.failedText', {
                                    count: failedCount,
                                    total: statusItems.length
                                })}
                            </Typography>

                            <Typography
                                sx={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto', mt: 1 }}
                                component="span"
                                variant="body2"
                            >
                                {t('helpMenu.contactSupportForAssistance')}
                            </Typography>
                        </Box>
                    ,
                    show: true
                });
            }
        } catch (error) {
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                error: error as RestError,
                show: true
            });
        }
    }

    async function onAction(action: UserMessageActionEnum, payload: INotificationMessageModel) {

        let autoClose: boolean = true;

        switch (action) {
            case UserMessageActionEnum.Download:

                if (payload.uri) {
                    fileDownloaderRef.current?.download(payload.uri);
                }

                break;

            case UserMessageActionEnum.Delete:
                confirmAndDeleteFormInstance(payload);
                break;

            case UserMessageActionEnum.Display: {

                storageService.session.set(MESSAGEIDKEY, payload.id);
                // User Story 53209: Make Code Changes per GAS Report
                // let url: string | undefined;

                if (payload.messageType === NotificationMessageTypeEnum.LiveDataStatusMessage) {
                    onDisplayLiveDataStatus(payload);
                } else if (payload.messageType === NotificationMessageTypeEnum.TrendingJSON) {

                    autoClose = false;
                    await onDisplayGraphView(payload);

                } else if (payload.messageType === NotificationMessageTypeEnum.QuickTrendJSON) {

                    autoClose = false;
                    await onDisplayGraphReportView(payload);

                } else if (payload.messageType === NotificationMessageTypeEnum.ReportingJSON) {

                    autoClose = false;
                    await onDisplayReportView(payload);
                } else if (payload.messageType === NotificationMessageTypeEnum.DataFileImportLog) {
                    autoClose = false;
                    await onDisplayDataFileImportLog(payload);
                }

                // User Story 53209: Make Code Changes per GAS Report
                // if (url) {
                //     navigateTo(url);
                // }
                break;
            }
        }

        if (autoClose) {
            onClose && onClose();
        }
    }

    useEffect(() => {
        const progress = progressState.items.find(o => o.notificationMsg?.status === StatusEnum.Completed);
        if (progress) {
            refetch();
        }
    }, [
        progressState.items,
        refetch
    ]);

    return (
        <>
            <LoadingZone isLoading={status === "loading"}>

                <List sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    maxHeight: '75%',
                    maxWidth: isPhone ? 310 : 600,
                    minWidth: isPhone ? undefined : 400,
                    bgcolor: 'background.paper'
                }}>

                    {listItems?.map((o, index) => (

                        <React.Fragment key={index}>
                            <UserMessageListItem
                                item={o}
                                onAction={onAction}
                                expanded={false} />
                            {
                                index < (data?.length ?? 0) - 1 &&
                                <Divider variant="inset" component="li" />
                            }
                        </React.Fragment>
                    ))}

                </List>

            </LoadingZone>

            <GraphViewDialog
                {...bindGraphViewDialog(graphViewDialog)}
            />

            <GraphReportViewDialog
                {...bindGraphReportViewDialog(graphReportViewDialog)}
            />

            <ReportViewDialog
                {...bindReportViewDialog(reportViewDialog)}
            />
            <FileDownloader ref={fileDownloaderRef} />
        </>
    );
}

